class LazyVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    });
    // We need to observe the video element as the host element has display: contents on it. IntersectionObserver won't work on an element that is set to display: contents
    this.observer.observe(this.video);
  }

  onEntry([entry], observer) {
    if (!entry.isIntersecting) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      this.video.removeAttribute("autoplay");
      this.video.pause();
    }

    if (this.video.dataset?.poster) {
      this.video.setAttribute("poster", this.video.dataset.poster);
    }

    for (const source in this.video.children) {
      const videoSource = this.video.children[source];

      if (
        typeof videoSource.tagName === "string" &&
        videoSource.tagName === "SOURCE" &&
        videoSource.dataset?.src
      ) {
        videoSource.src = videoSource.dataset.src;
      }
    }

    this.video.load();
    observer.unobserve(this.video);

    this.setAttribute("loaded", "");

    if (this.video.hasAttribute("autoplay") === false) {
      return;
    }
  }
}

if (!customElements.get("lazy-video")) {
  customElements.define("lazy-video", LazyVideo);
}

class LazyVideoControls extends HTMLElement {
  constructor() {
    super();

    this.els = {
      play: this.querySelectorAll('[data-lazy-video-controls-el="play"]'),
      mute: this.querySelectorAll('[data-lazy-video-controls-el="mute"]'),
    };

    this.externalEls = {
      section: '[data-lazy-video-controls-external-el="section"]',
    };

    this.setupListeners();
  }

  setupListeners() {
    this.handlePlayClick = this.handlePlayClick.bind(this);
    this.handleMuteClick = this.handleMuteClick.bind(this);

    this.els.play.forEach((el) => {
      el.addEventListener("click", this.handlePlayClick);
    });

    this.els.mute.forEach((el) => {
      el.addEventListener("click", this.handleMuteClick);
    });
  }

  handlePlayClick(event) {
    const section = event.target.closest(this?.externalEls?.section);
    if (!section) return;

    const videos = Array.from(section.querySelectorAll("lazy-video")).filter(
      (el) => el.hasAttribute("loaded"),
    );

    section.setAttribute(
      "data-paused",
      videos.some((el) => !el.video.paused),
    );

    videos.forEach((el) => {
      const video = el.video;
      if (video.paused) {
        if (video.readyState >= 2) {
          video.play();
          this.updatePlayPauseButton(true, section);
        } else {
          video.addEventListener(
            "canplay",
            () => {
              video.play();
              this.updatePlayPauseButton(true, section);
            },
            { once: true },
          );
          video.load();
        }
      } else {
        video.pause();
        this.updatePlayPauseButton(false, section);
      }
    });
  }

  handleMuteClick(event) {
    const section = event.target.closest(this?.externalEls?.section);
    if (!section) return;

    const videos = Array.from(section.querySelectorAll("lazy-video")).filter(
      (el) => el.hasAttribute("loaded"),
    );

    section.setAttribute(
      "data-muted",
      videos.some((el) => !el.video.muted),
    );

    videos.forEach((el) => {
      const video = el.video;
      if (video.muted) {
        video.muted = false;
        this.updateMuteButton(false, section);
      } else {
        video.muted = true;
        this.updateMuteButton(true, section);
      }
    });
  }

  updatePlayPauseButton(isPlaying, section) {
    const buttons = section.querySelectorAll(
      '[data-lazy-video-controls-el="play"]',
    );

    buttons.forEach((button) => {
      button.setAttribute("aria-pressed", isPlaying ? "false" : "true");

      const playText = this.querySelector(
        '[data-lazy-video-controls-el="play-text"]',
      );
      playText.textContent =
        isPlaying ? button.dataset.pause : button.dataset.play;
    });
  }

  updateMuteButton(isMuted, section) {
    const buttons = section.querySelectorAll(
      '[data-lazy-video-controls-el="mute"]',
    );

    buttons.forEach((button) => {
      button.setAttribute("aria-pressed", isMuted ? "true" : "false");

      const muteText = this.querySelector(
        '[data-lazy-video-controls-el="mute-text"]',
      );
      muteText.textContent =
        isMuted ? button.dataset.unmute : button.dataset.mute;
    });
  }
}

if (!customElements.get("lazy-video-controls")) {
  customElements.define("lazy-video-controls", LazyVideoControls);
}
